import React, { useState, useEffect, useMemo } from 'react';
import { Tag, ChevronRight, Calendar, Percent, Zap, Leaf, UtensilsCrossed, Package, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';

const ICON_MAP = {
    Percent,
    Zap,
    Leaf,
    UtensilsCrossed,
    Package,
    Tag,
    Sparkles,
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || Tag;

export default function FlyerDeals() {
    const [deals, setDeals] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchDeals = async () => {
            setIsLoading(true);
            try {
                const dealsRef = collection(db, 'flyers_deals');
                const q = query(dealsRef, orderBy('order'));
                const snapshot = await getDocs(q);
                const fetched = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(deal => deal.status !== false);
                setDeals(fetched);
            } catch (error) {
                console.error('Error fetching flyers & deals:', error);
                setDeals([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDeals();
    }, []);

    const categories = useMemo(() => {
        const unique = [...new Set(deals.map(d => d.category).filter(Boolean))];
        return ['All', ...unique];
    }, [deals]);

    const filtered = activeCategory === 'All'
        ? deals
        : deals.filter(d => d.category === activeCategory);

    return (
        <div className="bg-[#efefefce] min-h-screen pb-20 pt-8">
            <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">
                {/* ── PAGE HEADER ── */}
                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-1">
                        <Tag className="w-4 h-4 text-[#2C3E2B]" />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#2C3E2B]/60">Promotions</span>
                    </div>
                    <h1 className="text-3xl font-black text-[#2C3E2B] leading-tight">
                        Flyers &amp; Deals
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-lg">
                        Browse our latest promotions, exclusive discounts, and limited-time offers crafted for hospitality businesses.
                    </p>
                </div>

                {/* ── CATEGORY FILTER PILLS ── */}
                {categories.length > 1 && (
                    <div className="flex items-center gap-2 mb-8 flex-wrap">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-1.5 rounded-full text-xs font-bold transition border cursor-pointer ${activeCategory === cat
                                        ? 'bg-[#2C3E2B] text-white border-[#2C3E2B] shadow-sm'
                                        : 'bg-white text-[#2C3E2B] border-slate-200 hover:border-[#2C3E2B]/40'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── LOADING STATE ── */}
                {isLoading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[1, 2].map(i => (
                            <div key={i} className="h-80 rounded-[28px] bg-slate-200 animate-pulse" />
                        ))}
                    </div>
                )}

                {/* ── DEAL CARDS ── */}
                {!isLoading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filtered.map((deal) => {
                            const Icon = getIconComponent(deal.icon);
                            const shopLink = deal.productId
                                ? `/product/${deal.productId}`
                                : '/products';

                            // Parse "from-[#color1] to-[#color2]" → inline gradient
                            // Works for both Tailwind arbitrary values and plain color names
                            const gradientStyle = (() => {
                                if (!deal.gradient) return {};
                                const fromMatch = deal.gradient.match(/from-\[([^\]]+)\]/);
                                const toMatch   = deal.gradient.match(/to-\[([^\]]+)\]/);
                                const from = fromMatch ? fromMatch[1] : '#1a5276';
                                const to   = toMatch   ? toMatch[1]   : '#2980b9';
                                return { backgroundImage: `linear-gradient(to right, ${from}, ${to})` };
                            })();

                            return (
                                <div
                                    key={deal.id}
                                    className="relative overflow-hidden rounded-[28px] text-white shadow-xl group"
                                    style={gradientStyle}
                                >
                                    {/* Decorative glows */}
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
                                    <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                                    <div className="relative z-10 flex flex-col h-full p-7">
                                        {/* Top row: tag + badge */}
                                        <div className="flex items-start justify-between mb-4">
                                            <span className="text-xs font-bold bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20">
                                                {deal.tag}
                                            </span>
                                            {deal.badge && (
                                                <span className="text-xs font-black bg-white text-[#9E2A2B] px-3 py-1 rounded-full shadow">
                                                    {deal.badge}
                                                </span>
                                            )}
                                        </div>
                                        {/* Title */}
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-black leading-tight">{deal.title}</h2>
                                                <p className="text-xs font-semibold text-white/70">{deal.subtitle}</p>
                                            </div>
                                        </div>
                                        {/* Description */}
                                        <p className="text-sm text-white/80 leading-relaxed mb-4 max-w-xs">
                                            {deal.description}
                                        </p>
                                        {/* Product image */}
                                        {deal.image && (
                                            <div className="relative h-36 rounded-2xl overflow-hidden mb-4 bg-white/10">
                                                <img
                                                    src={deal.image}
                                                    alt={deal.title}
                                                    className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        )}
                                        {/* Footer */}
                                        <div className="flex items-center justify-between mt-auto">
                                            {deal.validTill && (
                                                <div className="flex items-center gap-1.5 text-white/60 text-xs font-semibold">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    {deal.validTill}
                                                </div>
                                            )}
                                            <Link
                                                to={shopLink}
                                                className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/20 text-white text-xs font-bold px-4 py-2 rounded-xl transition group/btn ml-auto"
                                            >
                                                Shop Now
                                                <ChevronRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* ── EMPTY STATE ── */}
                {!isLoading && filtered.length === 0 && (
                    <div className="bg-white border border-slate-200 rounded-[24px] p-12 text-center shadow-sm mt-4">
                        <div className="text-4xl mb-3">🎟️</div>
                        <h3 className="text-base font-bold text-[#2C3E2B]">No deals in this category</h3>
                        <p className="text-sm text-slate-500 mt-1">Check back soon for new promotions!</p>
                    </div>
                )}
            </div>
        </div>
    );
}
